import isEmpty from 'lodash/isEmpty';
import { arrayOf, bool, func, number, shape, string } from 'prop-types';
import React from 'react';

import { wrongSKUsError } from '../../generic/flash-messages/utils';
import { getTooltipButtonWithText } from '../../generic/Tooltip';
import { highlightedSkusShape } from '../../prop-types';
import api from '../../REST/api';
import { wrongSKUsErrorMessage } from '../constants';
import HELP_TEXTS from '../utils/help-texts';
import MissingSKUsConfirmationDialog from './MissingSKUsConfirmationDialog';
import WrongSKUsActionModal from './WrongSKUsActionModal';

const ButtonWithTooltip = getTooltipButtonWithText(HELP_TEXTS);

class CampaignPushButton extends React.PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      linesPendingAcknowledgement: [],
      showWrongSKUsModal: false,
      wrongSKUsError: undefined,
    };

    this.handleClick = this.handleClick.bind(this);
    this.handleDismiss = this.handleDismiss.bind(this);
    this.handleConfirm = this.handleConfirm.bind(this);
    this.handleWrongSKUsDismiss = this.handleWrongSKUsDismiss.bind(this);
    this.handleWrongSKUsConfirm = this.handleWrongSKUsConfirm.bind(this);
  }

  handleClick() {
    if (!this.props.pushInProgress) {
      this.props.onPushProgressChange(true);
      this.checkSKUs();
    }
  }

  handleDismiss() {
    this.props.onPushProgressChange(false);
    this.setState({ linesPendingAcknowledgement: [] });
  }

  handleConfirm() {
    const { onPush, onPushProgressChange } = this.props;

    this.setState({ linesPendingAcknowledgement: [] });

    onPush()
      .finally(() => {
        onPushProgressChange(false);
      })
      .catch(error => {
        if (error && wrongSKUsError(error)?.includes(wrongSKUsErrorMessage)) {
          this.setState({ showWrongSKUsModal: true, wrongSKUsError: error });
        }
      });
  }

  handleWrongSKUsDismiss() {
    this.props.onPushProgressChange(false);
    this.setState({ showWrongSKUsModal: false });
  }

  async handleWrongSKUsConfirm(updatedSKUs) {
    const { onChange, highlightedSkus, productCode } = this.props;
    onChange({
      target: {
        name: 'highlightedSkus',
        value: { ...highlightedSkus, [productCode]: updatedSKUs },
      },
    });
    this.setState({ showWrongSKUsModal: false });
    await this.props.campaignSave({ preventDefault() {} });
    await this.handleConfirm();
  }

  checkSKUs() {
    const { campaignLinesRequiringSKUs } = this.props;

    Promise.all(
      campaignLinesRequiringSKUs.map(spec =>
        api.getSkus(spec).then(
          response => ({ ...spec, skus: response }),
          error => ({ ...spec, skus: [], error }),
        ),
      ),
    ).then(lines => {
      const linesPendingAcknowledgement = lines.filter(({ skus }) =>
        isEmpty(skus),
      );
      if (isEmpty(linesPendingAcknowledgement)) {
        this.handleConfirm();
      } else {
        this.setState({ linesPendingAcknowledgement });
      }
    });
  }

  render() {
    const {
      onPush,
      campaignLinesRequiringSKUs,
      label,
      pushInProgress,
      onPushProgressChange,
      isDisabled,
      overCCPLimit,
      isAnyLineReady,
      isZmsAmPushable,
      productCode,
      isDraftCampaignEnabled,
      ...props
    } = this.props;
    const { linesPendingAcknowledgement, showWrongSKUsModal } = this.state;

    const getHelpKey = () => {
      if (isDraftCampaignEnabled) {
        return 'campaignPush';
      }
      if (productCode.includes('CCP') && !isZmsAmPushable) {
        return 'zmsAmPushDisabled';
      }
      if (isAnyLineReady && overCCPLimit) {
        return 'overCCPLimit';
      }
      return 'campaignPush';
    };

    return (
      <>
        <ButtonWithTooltip
          color=""
          {...props}
          size="sm"
          helpKey={getHelpKey()}
          section="actionButtons"
          onClick={this.handleClick}
          disabled={
            isDraftCampaignEnabled
              ? isDisabled || pushInProgress
              : isDisabled ||
                pushInProgress ||
                (productCode.includes('CCP') && !isZmsAmPushable)
          }
          showTooltip={isDisabled || pushInProgress}
        >
          {label}
          {pushInProgress && '…'}
        </ButtonWithTooltip>
        <MissingSKUsConfirmationDialog
          isOpen={pushInProgress && !isEmpty(linesPendingAcknowledgement)}
          onConfirm={this.handleConfirm}
          onDismiss={this.handleDismiss}
          linesPendingAcknowledgement={linesPendingAcknowledgement}
        />
        <WrongSKUsActionModal
          isOpen={showWrongSKUsModal}
          error={this.state.wrongSKUsError}
          onConfirm={this.handleWrongSKUsConfirm}
          onDismiss={this.handleWrongSKUsDismiss}
          highlightedSkus={this.props.highlightedSkus}
          productCode={productCode}
        />
      </>
    );
  }
}

CampaignPushButton.propTypes = {
  label: string.isRequired,
  onPush: func.isRequired,
  campaignLinesRequiringSKUs: arrayOf(
    shape({
      accountId: number.isRequired,
      brandIds: arrayOf(number.isRequired).isRequired,
      categories: arrayOf(string.isRequired).isRequired,
      gender: string.isRequired,
      country: string.isRequired,
      name: string.isRequired,
      startDate: string,
    }),
  ).isRequired,
  pushInProgress: bool.isRequired,
  onPushProgressChange: func.isRequired,
  isDisabled: bool.isRequired,
  overCCPLimit: bool.isRequired,
  isAnyLineReady: bool.isRequired,
  productCode: string.isRequired,
  isZmsAmPushable: bool,
  isDraftCampaignEnabled: bool.isRequired,
  highlightedSkus: highlightedSkusShape,
  onChange: func.isRequired,
  campaignSave: func.isRequired,
};

CampaignPushButton.defaultProps = {
  isZmsAmPushable: false,
  highlightedSkus: {},
};

export default CampaignPushButton;
